import type { Dayjs } from 'dayjs';
import type { DateRange, DateRanges } from './types';

import { memo, useCallback, useMemo } from 'react';

import { Box, IconButton, Typography } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';

import { Iconify } from 'src/components/iconify';

import {
  RangeAvailabilityCalendarDay,
  RangeAvailabilityCalendarProvider,
} from './range-availability-calendar-day';
import { isDateSelectable } from './utils';

const CALENDAR_DAY_SIZE = 36;
const CALENDAR_DAY_GAP = 2;
const CALENDAR_WEEKS = 6;
const CALENDAR_MONTH_WIDTH = 320;
const CALENDAR_GRID_WIDTH = 7 * CALENDAR_DAY_SIZE + 6 * CALENDAR_DAY_GAP;
const CALENDAR_WEEKDAY_ROW_HEIGHT = 40;
const CALENDAR_HEADER_HEIGHT = 56;
const CALENDAR_MONTH_GAP = 0;

/** Weekday row + up to 6 week rows (matches Figma grid). */
export const CALENDAR_GRID_MIN_HEIGHT =
  CALENDAR_WEEKDAY_ROW_HEIGHT +
  CALENDAR_WEEKS * CALENDAR_DAY_SIZE +
  (CALENDAR_WEEKS - 1) * CALENDAR_DAY_GAP;

export const CALENDAR_MONTH_MIN_HEIGHT = CALENDAR_HEADER_HEIGHT + CALENDAR_GRID_MIN_HEIGHT;
export { CALENDAR_MONTH_GAP, CALENDAR_MONTH_WIDTH };

interface RangeAvailabilityCalendarMonthProps {
  month: Dayjs;
  window: DateRange;
  preferred?: DateRanges;
  excluded?: DateRanges;
  selectedDates: Dayjs[];
  onDateClick: (date: Dayjs) => void;
  readOnly?: boolean;
  showPrevious?: boolean;
  showNext?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
}

export const RangeAvailabilityCalendarMonth = memo(
  ({
    month,
    window,
    preferred,
    excluded,
    selectedDates,
    onDateClick,
    readOnly = false,
    showPrevious = false,
    showNext = false,
    onPrevious,
    onNext,
  }: RangeAvailabilityCalendarMonthProps) => {
    const shouldDisableDate = useCallback(
      (date: Dayjs) => !isDateSelectable(date, window, excluded),
      [excluded, window]
    );

    const calendarContext = useMemo(
      () => ({
        window,
        preferred,
        excluded,
        selectedDates,
      }),
      [excluded, preferred, selectedDates, window]
    );

    return (
      <Box sx={{ width: CALENDAR_MONTH_WIDTH, flexShrink: 0, minHeight: CALENDAR_MONTH_MIN_HEIGHT }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: CALENDAR_HEADER_HEIGHT,
            px: 1.5,
            py: 2,
          }}
        >
          <IconButton
            size="small"
            onClick={onPrevious}
            disabled={!showPrevious}
            aria-label="Previous month"
            sx={{
              width: 40,
              height: 40,
              visibility: showPrevious ? 'visible' : 'hidden',
              color: 'text.primary',
            }}
          >
            <Iconify icon="eva:arrow-ios-back-fill" width={24} />
          </IconButton>

          <Typography variant="subtitle1" color="text.primary" sx={{ whiteSpace: 'nowrap' }}>
            {month.format('MMMM YYYY')}
          </Typography>

          <IconButton
            size="small"
            onClick={onNext}
            disabled={!showNext}
            aria-label="Next month"
            sx={{
              width: 40,
              height: 40,
              visibility: showNext ? 'visible' : 'hidden',
              color: 'text.primary',
            }}
          >
            <Iconify icon="eva:arrow-ios-forward-fill" width={24} />
          </IconButton>
        </Box>

        <RangeAvailabilityCalendarProvider value={calendarContext}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              pointerEvents: readOnly ? 'none' : 'auto',
              // px: `${(CALENDAR_MONTH_WIDTH - CALENDAR_GRID_WIDTH) / 2}px`,
            }}
          >
            <DateCalendar
            referenceDate={month}
            value={null}
            onChange={(date) => {
              if (date) {
                onDateClick(date);
              }
            }}
            shouldDisableDate={shouldDisableDate}
            disableHighlightToday
            showDaysOutsideCurrentMonth={false}
            fixedWeekNumber={CALENDAR_WEEKS}
            slots={{
              day: RangeAvailabilityCalendarDay,
            }}
            sx={{
              width: CALENDAR_GRID_WIDTH,
              minHeight: CALENDAR_GRID_MIN_HEIGHT,
              maxHeight: 'none',
              m: 0,
              p: 0,
              overflow: 'visible',
              bgcolor: 'background.paper',
              color: 'text.primary',
              '&.MuiDateCalendar-root': {
                width: CALENDAR_GRID_WIDTH,
                minHeight: CALENDAR_GRID_MIN_HEIGHT,
                m: 0,
                p: 0,
                bgcolor: 'background.paper',
              },
              '& .MuiPickersCalendarHeader-root': {
                display: 'none',
              },
              '& .MuiPickersFadeTransitionGroup-root, & .MuiDayCalendar-root': {
                m: 0,
                p: 0,
                width: '100%',
              },
              '& .MuiDayCalendar-header': {
                gap: '2px',
                justifyContent: 'center',
                m: 0,
                p: 0,
              },
              '& .MuiDayCalendar-weekDayLabel': {
                width: CALENDAR_DAY_SIZE,
                height: CALENDAR_WEEKDAY_ROW_HEIGHT,
                m: 0,
                typography: 'caption',
                color: 'text.secondary',
              },
              '& .MuiDayCalendar-weekContainer': {
                gap: `${CALENDAR_DAY_GAP}px`,
                justifyContent: 'center',
                m: 0,
                p: 0,
                minHeight: CALENDAR_DAY_SIZE,
              },
              '& .MuiPickersSlideTransition-root': {
                minHeight: CALENDAR_GRID_MIN_HEIGHT - CALENDAR_WEEKDAY_ROW_HEIGHT,
                overflow: 'visible',
              },
              '& .MuiDayCalendar-root': {
                minHeight: CALENDAR_GRID_MIN_HEIGHT,
              },
              '& .MuiDayCalendar-monthContainer': {
                width: '100%',
                m: 0,
                p: 0,
              },
              '& .MuiPickersDay-dayOutsideMonth': {
                visibility: 'hidden',
                pointerEvents: 'none',
              },
            }}
          />
          </Box>
        </RangeAvailabilityCalendarProvider>
      </Box>
    );
  }
);

RangeAvailabilityCalendarMonth.displayName = 'RangeAvailabilityCalendarMonth';
